import { useQuery } from '@tanstack/react-query';
import { Mail, UserCheck, UserPlus, UserX } from 'lucide-react';
import * as React from 'react';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@payment-plan/ui';
import { AdvisorFormDialog } from './AdvisorFormDialog';
import { useAdvisors, useResendInvitation, useToggleAdvisor } from './use-advisors';
import { apiClient } from '../../../lib/api-client';
import type { Advisor, Paginated, Project } from '../../../types/admin';
import { textoSegunAcceso, useAccesoAdmin } from '../acceso-admin';
import { InmobiliariaSelector, useClaveInmobiliaria } from '../inmobiliaria-activa/inmobiliaria-activa';

export function AdvisorsPage(): React.JSX.Element {
  const { data: asesores, isLoading, isError, refetch } = useAdvisors();
  const inmobiliaria = useClaveInmobiliaria();
  const { data: proyectos } = useQuery<Paginated<Project>>({
    queryKey: ['projects', inmobiliaria, 1, 100],
    queryFn: () => apiClient.projects.list({ page: 1, pageSize: 100 }),
  });
  const resend = useResendInvitation();
  const toggle = useToggleAdvisor();

  const [formOpen, setFormOpen] = React.useState(false);
  const [toToggle, setToToggle] = React.useState<Advisor | null>(null);
  const [feedback, setFeedback] = React.useState<string | null>(null);

  const acceso = useAccesoAdmin('usuario');
  const puedeCrear = acceso.crear;
  const puedeEditar = acceso.editar;
  const lista = asesores ?? [];
  const proyectosDisponibles = proyectos?.data ?? [];

  const abrirFormulario = (): void => {
    if (proyectosDisponibles.length === 0) {
      setFeedback(
        'No hay proyectos disponibles. Crea un proyecto antes de invitar asesores.',
      );
      return;
    }
    setFormOpen(true);
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="mx-auto w-full max-w-6xl space-y-6">
        <PageHeader
          eyebrow="Administración"
          title="Asesores"
          description={textoSegunAcceso(acceso, {
            gestion: 'Crea asesores, asígnales proyectos e invítalos a activar su cuenta.',
            consulta: 'Consulta los asesores de tu inmobiliaria y los proyectos que tienen asignados.',
          })}
          action={
          puedeCrear ? (
            <Button className="gap-2" onClick={abrirFormulario}>
              <UserPlus className="h-4 w-4" />
              Nuevo asesor
            </Button>
          ) : undefined
        }
      />

      <InmobiliariaSelector />

      {feedback && (
        <p role="status" className="rounded-lg border border-border/70 bg-surface-fade px-4 py-3 text-sm">
          {feedback}
        </p>
      )}

      {isLoading ? (
        <LoadingState message="Cargando asesores" />
      ) : isError ? (
        <ErrorState message="No fue posible cargar los asesores" onRetry={() => void refetch()} />
      ) : lista.length === 0 ? (
        <EmptyState
          title="Aún no hay asesores"
          description={textoSegunAcceso(acceso, {
            gestion: 'Crea el primer asesor y asígnale sus proyectos.',
            consulta: 'Todavía no hay asesores registrados en tu inmobiliaria.',
          })}
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-border/70 shadow-elegant">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left">Asesor</th>
                <th className="px-4 py-3 text-left">Contacto</th>
                <th className="px-4 py-3 text-left">Proyectos</th>
                <th className="px-4 py-3 text-left">Estado</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((asesor) => (
                <tr
                  key={asesor.id}
                  className="border-t border-border/60 transition-colors hover:bg-muted/30"
                >
                  <td className="px-4 py-3">
                    <p className="font-medium text-foreground">{asesor.nombre}</p>
                    <p className="text-xs text-muted-foreground">{asesor.correo}</p>
                  </td>
                  <td className="px-4 py-3 tabular-nums text-muted-foreground">{asesor.celular}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {asesor.asesoresProyecto.map((ap) => ap.proyecto.nombre).join(', ') || '—'}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={asesor.activo ? 'success' : 'secondary'}>
                      {asesor.activo ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {puedeEditar && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Reenviar invitación a ${asesor.nombre}`}
                              onClick={() =>
                                resend.mutate(asesor.id, {
                                  onSuccess: () =>
                                    setFeedback(`Invitación reenviada a ${asesor.nombre}.`),
                                })
                              }
                            >
                              <Mail className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Reenviar invitación</TooltipContent>
                        </Tooltip>
                      )}
                      {(asesor.activo ? acceso.desactivar : acceso.editar) && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`${asesor.activo ? 'Desactivar' : 'Activar'} ${asesor.nombre}`}
                              onClick={() => setToToggle(asesor)}
                            >
                              {asesor.activo ? (
                                <UserX className="h-4 w-4" />
                              ) : (
                                <UserCheck className="h-4 w-4" />
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>{asesor.activo ? 'Desactivar' : 'Activar'}</TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AdvisorFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        proyectos={proyectosDisponibles}
      />

      <ConfirmDialog
        open={Boolean(toToggle)}
        onOpenChange={(open) => {
          if (!open) {
            setToToggle(null);
          }
        }}
        title={toToggle?.activo ? 'Desactivar asesor' : 'Activar asesor'}
        description={
          toToggle
            ? `${toToggle.nombre} ${toToggle.activo ? 'no podrá iniciar sesión' : 'podrá iniciar sesión de nuevo'}.`
            : undefined
        }
        confirmText={toToggle?.activo ? 'Desactivar' : 'Activar'}
        variant={toToggle?.activo ? 'destructive' : 'default'}
        loading={toggle.isPending}
        onConfirm={() => {
          if (!toToggle) {
            return;
          }
          toggle.mutate(
            { id: toToggle.id, activo: !toToggle.activo },
            {
              onSuccess: () => {
                setFeedback(
                  `Asesor ${toToggle.activo ? 'desactivado' : 'activado'} correctamente.`,
                );
                setToToggle(null);
              },
            },
          );
        }}
      />
      </div>
    </TooltipProvider>
  );
}
